<template>
  <layout title="YouFace" :transparent="true" class="overflow-x-hidden">
    <highlightSection
      backgroundImage="/storage/images/products/face/face_matching.jpg"
      altImage="Decentralized Authentication Youverse"
      title="Face matching and biometric infrastructure"
      subtitle="A state-of-the-art solution engineered for fast, precise, and secure face matching."
      leftButton="See how it works"
      urlLeftButton="https://6992654.fs1.hubspotusercontent-na1.net/hubfs/6992654/01.%20ONE%20PAGERS/Youverse_YouFace.pdf"
      :imageLeft="true"
    />
    <section1
      title="YouFace: Advanced face matching with superior results"
      imageSrc="/storage/images/products/face/face_matching_api.png"
      imageAlt="Face Matching API"
    />
    <section2
      title="A reliable face API with seamless integration"
      :elements="[
        {
          title: `Industry leading accuracy`,
          text: `YouFace delivers exceptional accuracy, achieving a 1 in 1 million false acceptance rate with only 0.3% false rejection. This level of precision ensures trustworthy results, even in the most demanding use cases.`,
        },
        {
          title: `Compliance with higher standards`,
          text: `YouFace adheres to ISO/ICAO 19794-5 image quality metrics, ensuring consistent performance and interoperability with international biometric systems. It is also recognized as a top-tier performer in face-matching accuracy by NIST.`,
        },
        {
          title: `Developer-friendly integration`,
          text: `Integrating YouFace into your platform is straightforward, thanks to its powerful RESTful API and extensive documentation. This developer-first approach enables rapid implementation, saving you time and resources.`,
        },
      ]"
    />
    <div class="my-10">
      <cta
        title="Get started"
        text="Download the API and implement face matching into your app in minutes."
        buttonText="Book a Demo"
        buttonLink="/book-a-meeting"
        target="_blank"
      />
    </div>

    <section3
      title="See how YouFace works in your industry"
      :elements="[
        {
          title: `Banking`,
          text: `Accelerate onboarding, minimize fraud, and automate KYC/AML checks.`,
          seeMoreUrl: `/use-cases/banking`,
          imageSrc: `/storage/images/home/icons/banking.svg`,
          imageAlt: `Banking`,
          imageStyle: `w-20 h-20`,
        },
        {
          title: `iGaming`,
          text: `Simplify player verification, boost onboarding and ensure regulatory compliance.`,
          seeMoreUrl: `/use-cases/igaming`,
          imageSrc: `/storage/images/home/icons/igaming.svg`,
          imageAlt: `iGaming`,
          imageStyle: `w-20 h-20`,
        },
        {
          title: `Hospitality`,
          text: `Ensure operational efficiency, protect guests’ data, and eliminate check-in friction.`,
          seeMoreUrl: `/use-cases/hospitality`,
          imageSrc: `/storage/images/home/icons/hospitality.svg`,
          imageAlt: `Hospitality`,
          imageStyle: `w-20 h-20`,
        },
        {
          title: `Mobility`,
          text: `Onboard customers instantly, ensure robust driver’s verification, and gain your customers trust.`,
          seeMoreUrl: `/use-cases/mobility`,
          imageSrc: `/storage/images/home/icons/mobility.svg`,
          imageAlt: `Mobility`,
          imageStyle: `w-20 h-20`,
        },
      ]"
    />

    <textImageSection
      title="Choose your plan"
      text="Choose your plan and unlock the full potential of top-tier face matching."
      imageSrc="/storage/images/products/face/youverse_authentication_pricing.png"
      imageAlt="Biometric Authentication Id Verification"
      buttonText="Pick your plan"
      buttonLink="/pricing"
    />

    <latestPosts
      title="Read our latest articles and resources"
      :tags="['face matching']"
    />

    <div class="mt-20">
      <cta
        title="Start your free trial today"
        text="Explore YouFace’s features at no cost and experience the difference firsthand."
        buttonText="Contact Us"
        buttonLink="/company#contactus"
      />
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(
    /* webpackChunkName: "layout" */ "./../../Components/Layout/layout.vue"
  );

const highlightSection = () =>
  import(
    /* webpackChunkName: "highlightSection" */ "./../../Components/Layout/highlightSection.vue"
  );

const section1 = () =>
  import(
    /* webpackChunkName: "section1" */ "../../Components/Layout/Products/section1.vue"
  );

const section2 = () =>
  import(
    /* webpackChunkName: "section2" */ "../../Components/Layout/Products/section2.vue"
  );

const cta = () =>
  import(/* webpackChunkName: "cta" */ "./../../Components/Layout/cta.vue");

const section3 = () =>
  import(
    /* webpackChunkName: "section3" */ "../../Components/Layout/Products/section3.vue"
  );

const textImageSection = () =>
  import(
    /* webpackChunkName: "textImageSection" */ "../../Components/Layout/textImageSection.vue"
  );

const latestPosts = () =>
  import(
    /* webpackChunkName: "latestPosts" */ "./../../Components/Layout/latestPosts.vue"
  );

export default {
  components: {
    layout,
    highlightSection,
    section1,
    section2,
    cta,
    section3,
    textImageSection,
    latestPosts,
  },
  props: {
    errors: Object,
  },
};
</script>

